import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { skillsData } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const categories = ["All", "WordPress", "Languages", "Engineering"];

const SkillChip = ({ skill, index, isVisible }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, y: 20 }}
    animate={isVisible ? { opacity: 1, scale: 1, y: 0 } : {}}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    whileHover={{ scale: 1.08, y: -4, transition: { duration: 0.2 } }}
    className="group relative"
  >
    <div className="px-4 py-2.5 bg-white dark:bg-zinc-900/70 border border-zinc-200 dark:border-zinc-800/60 rounded-xl text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-colors duration-300 cursor-default shadow-sm dark:shadow-none">
      <span className="relative z-10">{skill.name}</span>
    </div>
  </motion.div>
);

const SkillsSection = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const filtered =
    activeFilter === "All"
      ? skillsData
      : skillsData.filter((s) => s.category === activeFilter);

  return (
    <section
      id="skills"
      className="relative py-28 lg:py-36 bg-white dark:bg-zinc-950 transition-colors duration-500"
      ref={sectionRef}
      aria-label="Skills and Expertise"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-zinc-300/60 dark:via-zinc-800/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionReveal animation="splitReveal">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">
              Skills & Expertise
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">
              Technical toolkit
            </h2>
            <p className="mt-4 text-zinc-500 text-lg max-w-2xl mx-auto">
              Years of working with WordPress at enterprise scale, contributing to
              open source, and building with modern web technologies.
            </p>
          </div>
        </SectionReveal>

        <SectionReveal animation="fadeUp" delay={0.15}>
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  activeFilter === cat
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25"
                    : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 border border-transparent hover:border-zinc-200 dark:hover:border-zinc-800"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </SectionReveal>

        <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {filtered.map((skill, i) => (
            <SkillChip key={skill.name} skill={skill} index={i} isVisible={isInView} />
          ))}
        </div>

        <SectionReveal animation="scaleUp" delay={0.4}>
          <div className="mt-20 max-w-2xl mx-auto">
            <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800/50">
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
                <span className="ml-3 text-xs text-zinc-400 dark:text-zinc-600 font-mono">skills.config</span>
              </div>
              <div className="p-5 font-mono text-sm space-y-1">
                <p className="text-zinc-400 dark:text-zinc-600">// Current focus areas</p>
                <p>
                  <span className="text-emerald-600/70 dark:text-emerald-400/70">const</span>{" "}
                  <span className="text-zinc-700 dark:text-zinc-300">stack</span>{" "}
                  <span className="text-zinc-400 dark:text-zinc-600">=</span>{" "}
                  <span className="text-zinc-400 dark:text-zinc-500">{"{}"[0]}</span>
                </p>
                <p className="pl-4">
                  <span className="text-zinc-500 dark:text-zinc-400">primary:</span>{" "}
                  <span className="text-emerald-600/60 dark:text-emerald-400/60">'WordPress + Gutenberg'</span>,
                </p>
                <p className="pl-4">
                  <span className="text-zinc-500 dark:text-zinc-400">languages:</span>{" "}
                  <span className="text-emerald-600/60 dark:text-emerald-400/60">'PHP, JS, React, TS'</span>,
                </p>
                <p className="pl-4">
                  <span className="text-zinc-500 dark:text-zinc-400">mission:</span>{" "}
                  <span className="text-emerald-600/60 dark:text-emerald-400/60">'Open Source'</span>,
                </p>
                <p><span className="text-zinc-400 dark:text-zinc-500">{"{}"[1]}</span>;</p>
              </div>
            </div>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default SkillsSection;
